import BaseRegulation from '../base_regulation';
import TempAccountMessageRegulationHelper from '../../helpers/temp_account_message_helper';
import _ from '../../../../utils/underscore';
import {KICK_REASON, KICK_TYPE} from '../../../player_messages_service/player_messages_consts';
import {CGP_ERROR_CODES} from '../../../../exceptions/cgp_error_codes';
import Event from '../../../../utils/event';
import {SYSTEM_EVENTS} from '../../../../events/events';
import {LOGIN_TYPE} from '../../../oauth/oauth_const';
import {SwedenStructuredLogging} from './logging/sweden_structured_logging';
import {CLOCK_LOCALIZATION} from '../../../clock/clock_service_consts';
import {Check, TimeSpan} from "@sparkware/app-utils";
import {CHANNEL_ELEMENTS} from "../../../../../../common/const/casinofied-analytics-consts";
import {AreaType, ControlCenterOpenSource} from "@sparkware/uc-sdk-core";
import {ToastCloseReason} from '../../../../../../app-react/services/toaster/toaster.service.interface';
import {GameChildishIconMode} from '../../../../../../common/enums/game-childish-icon-mode.enum';

const DEPOSIT_LIMIT_TYPES = {
    DAILY: 1,
    WEEKLY: 2,
    MONTHLY: 5
};

const PENDING_APP_PP = 27;

const LOGIN_MESSAGE_DURATION_SEC = 8;


export default class SwedenRegulation extends BaseRegulation {


    static CLASS = 'SwedenRegulation';


    // logging object
    _structuredLogger = null;

    /**
     *
     * @param params
     */
    init(params) {
        super.init(params);

        this._structuredLogger = SwedenStructuredLogging.create({
            serviceFactory: this.serviceFactory
        });

    }


    showSessionBalanceInAboutBox() {
        return true;
    }


    sessionTimerVisible() {
        return true;
    }

    realityCheckEnabled() {
        return !this.serviceFactory.integrationService().isMiniCip();
    }

    sessionTimerEnabled() {
        return true;
    }

    showResponsibleIcons() {
        return true;
    }

    getChildishIconMode() {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return GameChildishIconMode.UseLessNonChildishIcon;
        }

        return this.isVerifiedForRegulation()
            ? GameChildishIconMode.PreferLessNonChildishIcon
            : GameChildishIconMode.UseLessNonChildishIcon;
    }

    getRealityCheckInterval() {
        if (this.serviceFactory.skinService().useControlCenterForReminderInterval()) {
            const accountData = this.serviceFactory.userService().getAccountData();
            if (accountData && !Check.isNullOrUndefined(accountData.RealityCheckDataIntervalInMins)) {
                return TimeSpan.fromMinutes(accountData.RealityCheckDataIntervalInMins);
            }
        }
        return super.getRealityCheckInterval();
    }

    _realityCheckShouldBeDisplayedInGame() {
        return this.serviceFactory
            .gameService()
            .shouldShowRealityCheckMessageWhileGameIsOpen();
    }

    /**
     * Overrides base class
     * @param sessionTimer
     * @private
     */
    _handleRealityCheckMessage(sessionTimer) {
        const isGameRunning = this.serviceFactory.gameService().isGameRunning();
        if (isGameRunning) {
            if (!this._realityCheckShouldBeDisplayedInGame()) {
                return;
            }
        }

        this.serviceFactory.userService().getRealityCheckData().then(data => {

            this.serviceFactory.gameService().stopCurrentGameAutoplay();

            this._structuredLogger.realityCheckMessageSuccess(data, this.getCurrentSessionTime());

            this.serviceFactory.orbitComponentRenderService().showTimeSpentReminderWithBalance({
                playingTime: this.getCurrentSessionTime(),
                totalNetLoss: Check.isNullOrUndefined(data?.TotalNetLoss) ? 0 : data.TotalNetLoss,
                isInGame: isGameRunning,
                showPopupAboveSecondWebViewHybrid: true
            });
        }).catch(err => {

            this.serviceFactory.gameService().stopCurrentGameAutoplay();

            this._structuredLogger.realityCheckMessageError(err, this.getCurrentSessionTime().toString());

            this.serviceFactory.orbitComponentRenderService().showTimeSpentReminderWithBalance({
                playingTime: this.getCurrentSessionTime(),
                totalNetLoss: 0,
                isInGame: isGameRunning,
                showPopupAboveSecondWebViewHybrid: true
            });
        });
    }


    showTempAccountMessage() {
        TempAccountMessageRegulationHelper.create({
            serviceFactory: this.serviceFactory
        }).showTempAccountMessage({
            messageKey: 'sw_temp_account_message'
        });
    }

    /**
     * When got soft kick should open message when no open game.
     * @param kickParams
     */
    onKickEvent(kickParams) {
        let messageText = '';

        if (typeof kickParams !== 'object' || kickParams.KickType !== KICK_TYPE.SOFT_KICK) {
            return;
        }

        switch (kickParams.KickReasonId) {
            case KICK_REASON.KickUserDailyReached:
                messageText = this.getErrorMessage(CGP_ERROR_CODES.TIME_LIMIT_REACHED_DAILY);
                break;
            case KICK_REASON.KickUserWeeklyReached:
                messageText = this.getErrorMessage(CGP_ERROR_CODES.TIME_LIMIT_REACHED_WEEKLY);
                break;
            case KICK_REASON.KickUserMonthlyReached:
                messageText = this.getErrorMessage(CGP_ERROR_CODES.TIME_LIMIT_REACHED_MONTHLY);
                break;
            default:
                messageText = this.getErrorMessage(CGP_ERROR_CODES.GENERAL_ERROR_SUPPORT);
                break;
        }

        // no message means no popup
        if (!messageText) {
            return;
        }

        const shouldShowMessage = this._shouldShowMessage();

        if (shouldShowMessage) {
            this.serviceFactory.gameService().stopCurrentGameAutoplay();

            this.serviceFactory.messageManagerService().openMessage({
                text: messageText,
                closeButton: false,
                hasDefaultButton: false,
                buttons: [
                    {
                        name: this.serviceFactory.languageService().translation('mdp_ok'),
                        callback: () => {
                            this.serviceFactory.gameService().closeRunningGame();
                            Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, {category: 'time_limit_message', action: 'acknowledge'});
                        },
                        isClosable: true
                    }
                ]
            });
        }
    }

    _shouldShowMessage() {
        const isGameRunning = this.serviceFactory.gameService().isGameRunning();

        // open message when no game opened
        let shouldShowMessage = !isGameRunning;

        // if game is running
        if (isGameRunning) {
            const gameService = this.serviceFactory.gameService();
            // Open message when company supports it and game is run in free play mode
            if (gameService.isGameRunningInFreePlayMode() && gameService.shouldShowTimeLimitMessageWhileGameIsOpen()) {
                shouldShowMessage = true;
            }
        }
        return shouldShowMessage;
    }

    showLoginMessage() {
        let oAuth2Service = this.serviceFactory.oAuth2Service();
        if (!oAuth2Service.isAuthorized()) {
            this.logger.debug('User is not authorized. Login message should not be displayed');
            return;
        }

        if (oAuth2Service.getLoginType() === LOGIN_TYPE.AUTOLOGIN && oAuth2Service.getSourceProductPackage() !== PENDING_APP_PP) { // autologin
            this.logger.debug('User has been authorized by autologin without Pending Application product package');
            return;
        }

        let limitsData = _.safeJSON(oAuth2Service.extractTokenProperty('regulationMessages'), null);

        let messageParams = this._getLoginMessageParams(limitsData);

        if (!_.isNullOrUndefined(limitsData)) {
            if (!_.isNullOrUndefined(limitsData.DefinedDepositLimits)) {
                this._structuredLogger.loginMessageSuccess(limitsData);
            } else {
                this._structuredLogger.loginMessageError(limitsData.ErrorMessage);
            }
        } else {
            this._structuredLogger.loginMessageError();
        }

        this.serviceFactory.orbitComponentRenderService().showLoginMessageToast(
            {
                id: 'sweden_login_message',
                toastOptions: {
                    timeout: TimeSpan.fromSeconds(LOGIN_MESSAGE_DURATION_SEC)
                },
                contentProps: {
                    contentText: messageParams.body,
                    title: messageParams.title,
                },
                onToastClosed: (reason) => {
                    if (reason === ToastCloseReason.CloseButton) {
                        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, {
                            category: 'Login_message_toaster',
                            action: 'close',
                            label: 'Sweden'
                        });
                    }
                }
            }
        )

        return Promise.resolve();
    }

    _getLoginMessageParams(limitsData) {

        let languageService = this.serviceFactory.languageService();
        let currencyService = this.serviceFactory.currencyService();

        if (!limitsData) {
            limitsData = {}
        }

        if (!limitsData.DefinedDepositLimits || limitsData.DefinedDepositLimits.length === 0) {
            limitsData.DefinedDepositLimits = [
                {Type: DEPOSIT_LIMIT_TYPES.DAILY, Amount: null},
                {Type: DEPOSIT_LIMIT_TYPES.WEEKLY, Amount: null},
                {Type: DEPOSIT_LIMIT_TYPES.MONTHLY, Amount: null}
            ];

        }

        let abs = (amount) => {
            if (_.isNullOrUndefined(amount)) {
                return null;
            }
            return Math.abs(amount);
        };

        let formatAmount = (amount) => {
            if (_.isNullOrUndefined(amount)) {
                return '';
            } else {
                return currencyService.formatMoney(amount);
            }
        };


        let bindingParams = {
            net_loss: formatAmount(abs(limitsData.TotalNetLoss))
        };

        // prepare deposit limit messages
        limitsData.DefinedDepositLimits.forEach((depositLimit) => {

            switch (depositLimit.Type) {
                case DEPOSIT_LIMIT_TYPES.DAILY:
                    bindingParams.daily_limit = formatAmount(depositLimit.Amount);
                    break;
                case DEPOSIT_LIMIT_TYPES.WEEKLY:
                    bindingParams.weekly_limit = formatAmount(depositLimit.Amount);
                    break;
                case DEPOSIT_LIMIT_TYPES.MONTHLY:
                    bindingParams.monthly_limit = formatAmount(depositLimit.Amount);
                    break;
                default:
                    // maybe should throw an Exception here
                    break;
            }
        });
        // prepare net loss message
        // A negative amount means the player won more than he lost
        // so in case the total loss less than 0, won message will be displayed
        let lossMsgSchema = (limitsData.TotalNetLoss || 0) < 0 ? 'login_message_total_win' : 'login_message_total_loss';

        return {
            title: languageService.translation('login_message_title'),
            body: languageService.translation(lossMsgSchema, bindingParams)
        }
    }

    openLimits() {
        this._sendAnalytics('OpenLimits');
        this.serviceFactory.externalLinksProxyService().openGameLimits(AreaType.button, CHANNEL_ELEMENTS.gamesLimitsLobbyButton, ControlCenterOpenSource.RgPolicy);
    }



    _sendAnalytics(action, value) {
        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, {
            category: 'Regulation',
            action: action,
            label: 'Sweden',
            value: value
        });
    }


    getClockLocalization() {
        return CLOCK_LOCALIZATION.LOCAL;
    }

}
